import { Delete } from '@mui/icons-material';
import { Box, Chip, IconButton, Typography, styled } from '@mui/material';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import React, { PropsWithChildren, useEffect, useState } from 'react';

const Summary = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const SummaryRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const SummaryFooterRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  backgroundColor: theme.palette.background.default,
}));

const ActivePageChip = styled(Chip)(({ theme }) => ({
  height: '26px',
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const PageChip = styled(Chip)({
  height: '26px',
});

type PageProps = {
  title: string;
  totals: string;
};

const Page: React.FC<PropsWithChildren<PageProps>> = ({ children }) => (
  <>{children}</>
);

Page.displayName = 'MultiPageBuilder.Page';

type MultiPageBuilderType = React.FC<{
  summaryHeader: string;
  summaryFooterTitle: string;
  summaryFooterTotals: string;
  defaultActivePageIndex?: number;
  showSummaryOnly?: boolean;
  onDeletePage?: (index: number) => void;
  pageName?: string;
  children:
    | React.ReactElement<PageProps>
    | Array<React.ReactElement<PageProps>>;
}> & {
  Page: React.FC<PropsWithChildren<PageProps>>;
};

const MultiPageBuilder: MultiPageBuilderType = ({
  children,
  defaultActivePageIndex = 0,
  summaryHeader,
  summaryFooterTitle,
  summaryFooterTotals,
  showSummaryOnly = false,
  onDeletePage,
  pageName,
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(
    defaultActivePageIndex,
  );
  const deleteDisclosure = useDisclosure<{
    pageIndex: number;
    pageTitle: string;
  }>();

  let activePage: React.ReactElement<PageProps> | null = null;
  const sections: Array<{ title: string; totals: string }> = [];

  React.Children.forEach(children, (child, index) => {
    if (index === currentPageIndex) {
      activePage = child;
    }
    sections.push({
      title: child.props.title,
      totals: child.props.totals,
    });
  });

  useEffect(() => {
    if (defaultActivePageIndex) {
      setCurrentPageIndex(defaultActivePageIndex);
    }
  }, [defaultActivePageIndex]);

  const handleDeletePage = (index: number) => {
    if (sections.length <= 1) {
      return;
    }

    const totalItemsAfterDeletion = sections.length - 1;
    const targetIndex = index === 0 ? 0 : index - 1;
    const maxValidIndex = totalItemsAfterDeletion - 1;
    const finalIndex = Math.min(targetIndex, maxValidIndex);

    onDeletePage?.(index);
    setCurrentPageIndex(Math.max(0, finalIndex));
  };

  const handleDeletePageClick = () => {
    if (sections.length <= 1) {
      return;
    }

    deleteDisclosure.onOpen();
  };

  const handleConfirmDelete = () => {
    handleDeletePage(currentPageIndex);
    deleteDisclosure.onClose();
  };

  return (
    <Column spacing="md">
      {!showSummaryOnly && <div>{activePage}</div>}
      <Column spacing="md">
        <Typography variant="h4">{summaryHeader}</Typography>
        <Summary>
          {sections.map((section, index) => (
            <SummaryRow
              key={`${section.title}-${index}`}
              fullWidth
              justify="space-between"
              centered
            >
              <Row spacing="sm" centered>
                <Typography>{section.title}</Typography>
                {!showSummaryOnly ? (
                  currentPageIndex === index ? (
                    <>
                      <ActivePageChip
                        variant="filled"
                        label="This page"
                        size="medium"
                      />
                      {onDeletePage && sections.length > 1 && (
                        <IconButton
                          onClick={() => handleDeletePageClick()}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <PageChip
                      variant="outlined"
                      label="Click to view page"
                      size="medium"
                      color="primary"
                      onClick={() => setCurrentPageIndex(index)}
                    />
                  )
                ) : null}
              </Row>
              <Typography fontWeight="bold">{section.totals}</Typography>
            </SummaryRow>
          ))}
          <SummaryFooterRow fullWidth justify="space-between" centered>
            <Typography fontWeight="bold">{summaryFooterTitle}</Typography>
            <Typography fontWeight="bold">{summaryFooterTotals}</Typography>
          </SummaryFooterRow>
        </Summary>
      </Column>

      <ConfirmDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title={`Delete ${pageName ? pageName : 'page'}`}
        content={`Are you sure you want to delete this ${
          pageName ? pageName : 'page'
        }? This action cannot be undone.`}
        actions={{
          confirm: {
            label: `Delete ${pageName ? pageName : 'page'}`,
            onClick: handleConfirmDelete,
          },
        }}
      />
    </Column>
  );
};

MultiPageBuilder.Page = Page;

export default MultiPageBuilder;
